import { makeAutoObservable, runInAction } from 'mobx';
import { io, Socket } from 'socket.io-client';
import { AppNotification, gatewayConfig } from '@app/common';
import type { RootStore } from './root-store';
import { notificationFromApi } from '../adapters/notification-api-adapter';
import { NotificationDialogStore } from './notification-dialog-store';

const DEFAULT_LIMIT = 20;
const MAX_LIMIT = 100;
const SOCKET_REFRESH_DEBOUNCE_MS = 200;

export class NotificationStore {
    rootStore: RootStore;

    notificationDialogStore: NotificationDialogStore;

    items: AppNotification[] = [];

    total = 0;

    revision = 0;

    unreadCount = 0;

    loadedCount = 0;

    loading = false;

    private notificationSocket: Socket | null = null;

    private refreshTimer: NodeJS.Timeout | null = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        this.notificationDialogStore = new NotificationDialogStore(this);
        makeAutoObservable(this, {
            rootStore: false,
            notificationDialogStore: false,
        });
    }

    get hasMore(): boolean {
        return this.loadedCount < this.total;
    }

    async init(): Promise<void> {
        await this.fetch();
        this.connect();
    }

    async fetch(limit: number = DEFAULT_LIMIT): Promise<void> {
        await this.fetchPage(Math.min(limit, MAX_LIMIT), 0, false);
    }

    async refresh(): Promise<void> {
        this.revision += 1;
        const requestedCount = Math.max(this.loadedCount, DEFAULT_LIMIT);
        this.loading = true;
        try {
            const notifications: AppNotification[] = [];
            let total = 0;
            let unreadCount = 0;

            for (let offset = 0; offset < requestedCount; offset += MAX_LIMIT) {
                // Keep page requests sequential to avoid a burst of list and count queries.
                // eslint-disable-next-line no-await-in-loop
                const result = await this.rootStore.apiClient.notificationsController.notificationControllerList({
                    limit: Math.min(MAX_LIMIT, requestedCount - offset),
                    offset,
                });
                notifications.push(...result.items.map(notificationFromApi));
                total = result.total;
                unreadCount = result.unreadCount;

                if (
                    notifications.length >= total ||
                    result.items.length < Math.min(MAX_LIMIT, requestedCount - offset)
                ) {
                    break;
                }
            }

            runInAction(() => {
                const notificationIds = new Set<string>();
                this.items = notifications.filter((notification) => {
                    if (notificationIds.has(notification.id)) {
                        return false;
                    }
                    notificationIds.add(notification.id);
                    return true;
                });
                this.loadedCount = this.items.length;
                this.total = total;
                this.unreadCount = unreadCount;
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    async loadMore(): Promise<void> {
        if (this.loading || !this.hasMore) {
            return;
        }
        await this.fetchPage(DEFAULT_LIMIT, this.loadedCount, true);
    }

    async markAsRead(id: string): Promise<void> {
        await this.rootStore.apiClient.notificationsController.notificationControllerMarkAsRead({ id });
        await this.refresh();
    }

    async markAllAsRead(): Promise<void> {
        await this.rootStore.apiClient.notificationsController.notificationControllerMarkAllAsRead();
        await this.refresh();
    }

    private async fetchPage(limit: number, offset: number, append: boolean): Promise<void> {
        this.loading = true;
        try {
            const result = await this.rootStore.apiClient.notificationsController.notificationControllerList({
                limit,
                offset,
            });
            const notifications = result.items.map(notificationFromApi);
            runInAction(() => {
                const currentIds = new Set(this.items.map((notification) => notification.id));
                this.items = append
                    ? [...this.items, ...notifications.filter((notification) => !currentIds.has(notification.id))]
                    : notifications;
                this.loadedCount = append ? this.loadedCount + result.items.length : result.items.length;
                this.total = result.total;
                this.unreadCount = result.unreadCount;
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    private connect(): void {
        if (this.notificationSocket) {
            return;
        }

        const jwtToken = this.rootStore.cookieService.getJwtToken();
        if (!jwtToken) {
            return;
        }

        this.notificationSocket = io(`${process.env.NEXT_PUBLIC_SOCKET_URL}/${gatewayConfig().channels.notification}`, {
            auth: { authorization: jwtToken },
            reconnection: true,
            secure: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1_000,
            transports: ['websocket'],
        });
        this.notificationSocket.on(gatewayConfig().events.notification.pub.changed, () => this.scheduleRefresh());
    }

    private scheduleRefresh(): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }
        this.refreshTimer = setTimeout(() => {
            this.refresh().catch((error) => {
                // eslint-disable-next-line no-console
                console.error(error);
            });
        }, SOCKET_REFRESH_DEBOUNCE_MS);
    }
}
